import React, { useEffect, useState } from 'react';
import api from '../api/api';
import { Card, Table, Form, Button, Alert, Row, Col } from 'react-bootstrap';
import { Toast, ToastContainer } from 'react-bootstrap';
import { useToast } from './Toasts';
import './UserInterface.css'; // Für benutzerdefiniertes CSS

function UserInterface({ darkMode, cardBgClass, isAdmin }) {
    const { notify } = useToast();
    const [users, setUsers] = useState([]);
    const [currentUser, setCurrentUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editingUser, setEditingUser] = useState(null);
    const [passwordMessage, setPasswordMessage] = useState(null);

    const [showPasswordForm, setShowPasswordForm] = useState(false);
    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [repeatPassword, setRepeatPassword] = useState('');
    const [toastMessage, setToastMessage] = useState('');
    const [showToast, setShowToast] = useState(false);
    const [toastBG, setToastBG] = useState('');

    const [userConnections, setUserConnections] = useState({});
    const [oauthLinks, setOauthLinks] = useState({});
    const [allUserConnections, setAllUserConnections] = useState({});
    const [botConnections, setBotConnections] = useState({});


    useEffect(() => {
        const fetchSessionAndUsers = async () => {
            try {
                const sessionRes = await api.get('/session');
                setCurrentUser(sessionRes.data.user);

                if (sessionRes.data.is_admin) {
                    const userRes = await api.get('/user-list');
                    setUsers(userRes.data);

                    // Lade Verbindungen für jeden Benutzer
                    const connectionsMap = {};
                    for (const user of userRes.data) {
                        try {
                            const connRes = await api.get(`/user/${user.benutzername}/connections`);
                            connectionsMap[user.benutzername] = connRes.data.services || [];
                        } catch (err) {
                            connectionsMap[user.benutzername] = [];
                        }
                    }
                    setAllUserConnections(connectionsMap);

                    // Lade Status der Bot-eigenen Verbindungen (Twitch/Kick)
                    await fetchBotConnections();
                }

                // Lade OAuth-Links
                try {
                    const linksRes = await api.get('/oauth-links');
                    setOauthLinks(linksRes.data);
                } catch (err) {
                    console.error('Fehler beim Laden der OAuth-Links:', err);
                }

                // Lade Benutzer-Verbindungen
                try {
                    const connRes = await api.get('/user-connections');
                    setUserConnections(connRes.data.connections || {});
                } catch (err) {
                    console.error('Fehler beim Laden der Verbindungen:', err);
                }
            } catch (err) {
                console.error('Fehler beim Laden:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchSessionAndUsers();
    }, []);

    const handleDeleteUser = async (benutzername) => {
        if (!window.confirm(`Möchtest du den Benutzer "${benutzername}" wirklich löschen?`)) return;

        try {
            const res = await api.post('/delete-user', { benutzername });
            if (res.data.status === 'user_deleted') {
                setUsers(users.filter((u) => u.benutzername !== benutzername));
            } else {
                notify('Löschen fehlgeschlagen: ' + (res.data.error || 'Unbekannter Fehler'), 'danger');
            }
        } catch (err) {
            console.error('Fehler beim Löschen:', err);
            notify('Benutzer konnte nicht gelöscht werden.', 'danger');
        }
    };

    const handleResetPassword = async (benutzername, email) => {
        if (!window.confirm(`Passwort für "${benutzername}" zurücksetzen?`)) return;
        try {
            const res = await api.post('/reset-password', { benutzername });
            if (res.data.status === 'passwort_gesendet') {
                notify(`Neues Passwort wurde an ${email} gesendet.`);
            } else {
                notify('Zurücksetzen fehlgeschlagen: ' + (res.data.error || 'Unbekannter Fehler'), 'danger');
            }
        } catch (err) {
            console.error('Fehler beim Zurücksetzen:', err);
            notify('Passwort konnte nicht zurückgesetzt werden.', 'danger');
        }
    };

    const handlePasswordChange = async () => {
        setPasswordMessage(null);

        if (!oldPassword || !newPassword || !repeatPassword) {
            setPasswordMessage({ type: 'danger', text: 'Bitte alle Felder ausfüllen.' });
            setToastMessage('⚠️ Bitte alle Felder ausfüllen.');
            setShowToast(true);
            setToastBG('warning');
            return;
        }
        if (newPassword !== repeatPassword) {
            setPasswordMessage({ type: 'danger', text: 'Die neuen Passwörter stimmen nicht überein.' });
            setToastMessage('❌ Die neuen Passwörter stimmen nicht überein.');
            setShowToast(true);
            setToastBG('danger');
            return;
        }

        try {
            await api.post('/password', {
                benutzername: currentUser.benutzername,
                altes_passwort: oldPassword,
                neues_passwort: newPassword
            });
            setToastMessage('✅ Passwort erfolgreich geändert.');
            setShowToast(true);
            setToastBG('success');
            setOldPassword('');
            setNewPassword('');
            setRepeatPassword('');
            setShowPasswordForm(false);
        } catch (err) {
            setPasswordMessage({ type: 'danger', text: '❌ Fehler beim Ändern des Passworts.' });
            setToastMessage('❌ Fehler beim Ändern des Passworts.');
            setShowToast(true);
            setToastBG('danger');
        }
    };

    const startEdit = (user) => setEditingUser({ ...user });
    const cancelEdit = () => setEditingUser(null);

    const saveEdit = async () => {
        if (!editingUser?.benutzername) return;

        try {
            const response = await api.post('/update-user', {
                id: editingUser.id,
                benutzername: editingUser.benutzername,
                email: editingUser.email,
                ip: editingUser.ip || null,
                is_admin: editingUser.is_admin ? 1 : 0
            });

            if (response.data.status === 'user_updated') {
                const updatedUsers = users.map((u) =>
                    u.id === editingUser.id ? editingUser : u
                );
                setUsers(updatedUsers);
            } else {
                notify('Aktualisieren fehlgeschlagen: ' + (response.data.error || 'Unbekannter Fehler'), 'danger');
            }
        } catch (err) {
            console.error('Update-Fehler:', err);
            notify('Benutzer konnte nicht gespeichert werden.', 'danger');
        }

        setEditingUser(null);
    };



    // Wartet bis das OAuth-Popup vom Benutzer geschlossen wurde (Login abgeschlossen
    // oder abgebrochen), bevor der tatsächliche Verbindungsstatus geprüft wird.
    // Vorher wurde nach fixen 2 Sekunden blind "verbunden!" angezeigt - unabhängig
    // davon, ob der Flow im Popup überhaupt erfolgreich war (z.B. bei Redirect-URI-
    // Fehlern beim Provider).
    const waitForPopupClose = (popup) => new Promise((resolve) => {
        if (!popup) {
            resolve();
            return;
        }
        const timer = setInterval(() => {
            if (popup.closed) {
                clearInterval(timer);
                resolve();
            }
        }, 500);
        // Sicherheitsnetz, falls das Popup nie als "closed" erkannt wird
        setTimeout(() => {
            clearInterval(timer);
            resolve();
        }, 5 * 60 * 1000);
    });

    const handleConnectService = (service) => {
        // Popup MUSS synchron im Klick-Handler geöffnet werden (leer, ohne await davor),
        // sonst blockieren Chrome/Firefox/Safari das Fenster stillschweigend, weil es
        // nicht mehr direkt an die Nutzerinteraktion gebunden ist.
        const popup = window.open('', `oauth-${service}`, 'width=600,height=700');
        const label = service.charAt(0).toUpperCase() + service.slice(1);

        (async () => {
            // Frischen Link holen statt des beim Seitenladen zwischengespeicherten -
            // State/PKCE haben serverseitig nur 10 Minuten TTL.
            let link = oauthLinks[service];
            try {
                const linksRes = await api.get('/oauth-links');
                setOauthLinks(linksRes.data);
                link = linksRes.data?.[service] || link;
            } catch (err) {
                console.error('Fehler beim Laden der OAuth-Links:', err);
            }

            if (!link) {
                popup?.close();
                setToastMessage(`❌ OAuth-Link für ${service} nicht verfügbar`);
                setShowToast(true);
                setToastBG('danger');
                return;
            }
            if (!popup) {
                setToastMessage(`⚠️ Popup wurde vom Browser blockiert. Bitte Popups für diese Seite erlauben und erneut versuchen.`);
                setShowToast(true);
                setToastBG('warning');
                return;
            }
            popup.location.href = link;

            await waitForPopupClose(popup);
            try {
                const res = await api.get('/user-connections');
                const connections = res.data.connections || {};
                setUserConnections(connections);
                if (connections[service]) {
                    setToastMessage(`✅ ${label} verbunden!`);
                    setToastBG('success');
                } else {
                    setToastMessage(`❌ Verbindung zu ${label} wurde nicht abgeschlossen. Bitte erneut versuchen.`);
                    setToastBG('danger');
                }
                setShowToast(true);
            } catch (err) {
                console.error('Fehler beim Neuladen:', err);
                setToastMessage(`❌ Fehler beim Prüfen der Verbindung zu ${label}`);
                setShowToast(true);
                setToastBG('danger');
            }
        })();
    };

    const handleDisconnectService = async (service) => {
        if (!window.confirm(`Soll die Verbindung zu ${service} getrennt werden?`)) return;

        try {
            await api.post('/oauth/disconnect', { service });
            setToastMessage(`✅ ${service} wurde getrennt`);
            setShowToast(true);
            setToastBG('success');

            // Lade Verbindungen neu
            const connRes = await api.get('/user-connections');
            setUserConnections(connRes.data.connections || {});
        } catch (err) {
            console.error('Disconnect-Fehler:', err);
            setToastMessage(`❌ Fehler beim Trennen`);
            setShowToast(true);
            setToastBG('danger');
        }
    };

    const fetchBotConnections = async () => {
        try {
            const res = await api.get('/bot-connections');
            const map = {};
            (res.data || []).forEach((c) => { map[c.platform] = c; });
            setBotConnections(map);
        } catch (err) {
            console.error('Fehler beim Laden der Bot-Verbindungen:', err);
        }
    };

    const handleBotConnect = async (platform) => {
        // Popup MUSS synchron (ohne vorheriges await) geöffnet werden, sonst blockieren
        // Chrome/Firefox/Safari es, weil der spätere window.open() nach dem "await
        // api.post(...)" nicht mehr als direkte Nutzerinteraktion gilt - das war der
        // Grund, warum zuletzt gar kein Popup mehr aufging.
        const popup = window.open('', `bot-oauth-${platform}`, 'width=600,height=700');
        const label = platform.charAt(0).toUpperCase() + platform.slice(1);
        try {
            const res = await api.post(`/bot-connections/${platform}/init`);
            const link = res.data?.authorize_url;
            if (!link) {
                popup?.close();
                setToastMessage(`❌ Konnte Verbindung für ${platform} nicht starten`);
                setShowToast(true);
                setToastBG('danger');
                return;
            }
            if (!popup) {
                setToastMessage(`⚠️ Popup wurde vom Browser blockiert. Bitte Popups für diese Seite erlauben und erneut versuchen.`);
                setShowToast(true);
                setToastBG('warning');
                return;
            }
            popup.location.href = link;
            await waitForPopupClose(popup);
            await fetchBotConnections();
            try {
                const statusRes = await api.get('/bot-connections');
                const conn = (statusRes.data || []).find((c) => c.platform === platform);
                if (conn?.status === 'connected') {
                    setToastMessage(`✅ Bot-${label} verbunden!`);
                    setToastBG('success');
                } else {
                    setToastMessage(`❌ Bot-Verbindung zu ${label} wurde nicht abgeschlossen. Bitte erneut versuchen.`);
                    setToastBG('danger');
                }
            } catch (err) {
                setToastMessage(`❌ Fehler beim Prüfen der Bot-Verbindung zu ${label}`);
                setToastBG('danger');
            }
            setShowToast(true);
        } catch (err) {
            popup?.close();
            console.error('Bot-Connect-Fehler:', err);
            setToastMessage(`❌ Fehler beim Verbinden von ${platform}`);
            setShowToast(true);
            setToastBG('danger');
        }
    };

    const handleBotDisconnect = async (platform) => {
        if (!window.confirm(`Soll die Bot-Verbindung zu ${platform} getrennt werden? Der Stream-Bot kann dann nicht mehr auf ${platform} zugreifen, bis neu verbunden wird.`)) return;

        try {
            await api.post(`/bot-connections/${platform}/disconnect`);
            setToastMessage(`✅ Bot-Verbindung zu ${platform} getrennt`);
            setShowToast(true);
            setToastBG('success');
            await fetchBotConnections();
        } catch (err) {
            console.error('Bot-Disconnect-Fehler:', err);
            setToastMessage(`❌ Fehler beim Trennen`);
            setShowToast(true);
            setToastBG('danger');
        }
    };

    if (loading || !currentUser) return <div>Lade Benutzerdaten...</div>;

    return (
        <>
            <Card className={`p-3 ${cardBgClass}`}>
                <h4>👤 Benutzerbereich</h4>
                <p className={isAdmin ? 'text-success' : 'text-muted'}>
                    {isAdmin
                        ? '🔐 Du bist als Administrator angemeldet.'
                        : '👤 Du bist als Benutzer angemeldet.'}
                </p>

                {isAdmin && (
                    <>
                        <h5>📋 Benutzerliste</h5>
                        <div className={`user-table-scroll ${darkMode ? 'user-table-dark' : 'user-table-light'}`}>
                            <Table striped bordered hover variant={darkMode ? 'dark' : 'light'}>
                                <thead>
                                    <tr>
                                        <th>Benutzername</th>
                                        <th>E-Mail</th>
                                        <th>IP</th>
                                        <th>Admin</th>
                                        <th>Dienste</th>
                                        <th>Aktionen</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {users.map((u, i) => (
                                        <tr key={i}>
                                            <td>
                                                {editingUser?.id === u.id ? (
                                                    <Form.Control
                                                        value={editingUser.benutzername}
                                                        onChange={(e) =>
                                                            setEditingUser({ ...editingUser, benutzername: e.target.value })
                                                        }
                                                    />
                                                ) : (
                                                    u.benutzername
                                                )}
                                            </td>
                                            <td>
                                                {editingUser?.id === u.id ? (
                                                    <Form.Control
                                                        value={editingUser.email}
                                                        onChange={(e) =>
                                                            setEditingUser({ ...editingUser, email: e.target.value })
                                                        }
                                                    />
                                                ) : (
                                                    u.email
                                                )}
                                            </td>
                                            <td>
                                                {editingUser?.id === u.id ? (
                                                    <Form.Control
                                                        value={editingUser.ip || ''}
                                                        onChange={(e) =>
                                                            setEditingUser({ ...editingUser, ip: e.target.value })
                                                        }
                                                    />
                                                ) : (
                                                    u.ip || '-'
                                                )}
                                            </td>
                                            <td>
                                                {editingUser?.id === u.id ? (
                                                    <Form.Check
                                                        type="checkbox"
                                                        checked={!!editingUser.is_admin}
                                                        onChange={(e) =>
                                                            setEditingUser({ ...editingUser, is_admin: e.target.checked })
                                                        }
                                                    />
                                                ) : u.is_admin ? (
                                                    '✅'
                                                ) : (
                                                    '❌'
                                                )}
                                            </td>
                                            <td>
                                                {allUserConnections[u.benutzername]?.length > 0 ? (
                                                    <small>
                                                        {allUserConnections[u.benutzername].map((service) => (
                                                            <span key={service} className="badge me-1" style={{ backgroundColor: service === 'twitch' ? '#1f4788' : service === 'kick' ? '#2d5f4f' : '#4a2929' }}>
                                                                {service === 'twitch' ? '📺' : service === 'kick' ? '🎮' : '🎥'} {service}
                                                            </span>
                                                        ))}
                                                    </small>
                                                ) : (
                                                    <small className="text-muted">-</small>
                                                )}
                                            </td>
                                            <td>
                                                {editingUser?.id === u.id ? (
                                                    <>
                                                        <Button size="sm" variant="success" onClick={saveEdit}>
                                                            💾
                                                        </Button>{' '}
                                                        <Button size="sm" variant="secondary" onClick={cancelEdit}>
                                                            ✖
                                                        </Button>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Button
                                                            size="sm"
                                                            variant="outline-primary"
                                                            onClick={() => startEdit(u)}
                                                        >
                                                            ✏️
                                                        </Button>{' '}
                                                        {u.benutzername !== currentUser.benutzername && (
                                                            <>
                                                                <Button
                                                                    size="sm"
                                                                    variant="outline-danger"
                                                                    onClick={() => handleDeleteUser(u.benutzername)}
                                                                >
                                                                    🗑️
                                                                </Button>{' '}
                                                                <Button
                                                                    size="sm"
                                                                    variant="outline-warning"
                                                                    onClick={() =>
                                                                        handleResetPassword(u.benutzername, u.email)
                                                                    }
                                                                >
                                                                    🔄
                                                                </Button>
                                                            </>
                                                        )}
                                                    </>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>

                        <hr />
                        <h5>🤖 Bot-Verbindungen (Streaming-Account)</h5>
                        <p className="text-muted"><small>Zugangsdaten, mit denen der Stream-Bot selbst auf Twitch/Kick zugreift (Titel ändern, Chat, Raids, ...). Getrennt von den persönlichen Verknüpfungen der Dashboard-User oben.</small></p>
                        <Row className="mb-3">
                            {['twitch', 'kick'].map((platform) => {
                                const conn = botConnections[platform];
                                const isConnected = conn?.status === 'connected';
                                const hasError = conn?.status === 'error';
                                const bg = isConnected
                                    ? (platform === 'twitch' ? (darkMode ? '#1f4788' : '#e3f2fd') : (darkMode ? '#2d5f4f' : '#e8f5e9'))
                                    : (darkMode ? '#333' : '#f5f5f5');
                                return (
                                    <Col md={4} className="mb-2" key={platform}>
                                        <Card className="p-3" style={{ backgroundColor: bg, borderColor: isConnected ? '#1f4788' : hasError ? '#dc3545' : (darkMode ? '#495057' : '#ddd') }}>
                                            <div className="text-center">
                                                <h6>{platform === 'twitch' ? '📺 Twitch' : '🎮 Kick'} (Bot)</h6>
                                                {isConnected ? (
                                                    <>
                                                        <Alert variant="success" className="mb-2">
                                                            <small>✅ Verbunden</small>
                                                        </Alert>
                                                        <Button size="sm" variant="outline-danger" onClick={() => handleBotDisconnect(platform)}>
                                                            🔌 Trennen
                                                        </Button>
                                                    </>
                                                ) : (
                                                    <>
                                                        {hasError && (
                                                            <Alert variant="danger" className="mb-2">
                                                                <small>⚠️ {conn?.last_error || 'Verbindung fehlerhaft - Reconnect nötig'}</small>
                                                            </Alert>
                                                        )}
                                                        <Button size="sm" variant="primary" onClick={() => handleBotConnect(platform)}>
                                                            🔗 {hasError ? 'Neu verbinden' : 'Verbinden'}
                                                        </Button>
                                                    </>
                                                )}
                                            </div>
                                        </Card>
                                    </Col>
                                );
                            })}
                        </Row>
                    </>
                )}

                <hr />
                <h5>🔗 Stream-Dienste verbinden</h5>
                <p className="text-muted"><small>Verbinde deine Konten mit Streaming-Diensten</small></p>
                <Row className="mb-3">
                    <Col md={4} className="mb-2">
                        <Card className="p-3" style={{ backgroundColor: userConnections.twitch ? (darkMode ? '#1f4788' : '#e3f2fd') : (darkMode ? '#333' : '#f5f5f5'), borderColor: userConnections.twitch ? '#1f4788' : (darkMode ? '#495057' : '#ddd') }}>
                            <div className="text-center">
                                <h6>📺 Twitch</h6>
                                {userConnections.twitch ? (
                                    <>
                                        <Alert variant="success" className="mb-2">
                                            <small>✅ Verbunden</small>
                                        </Alert>
                                        <Button size="sm" variant="outline-danger" onClick={() => handleDisconnectService('twitch')}>
                                            🔌 Trennen
                                        </Button>
                                    </>
                                ) : (
                                    <Button size="sm" variant="primary" onClick={() => handleConnectService('twitch')}>
                                        🔗 Verbinden
                                    </Button>
                                )}
                            </div>
                        </Card>
                    </Col>
                    <Col md={4} className="mb-2">
                        <Card className="p-3" style={{ backgroundColor: userConnections.kick ? (darkMode ? '#2d5f4f' : '#e8f5e9') : (darkMode ? '#333' : '#f5f5f5'), borderColor: userConnections.kick ? '#2d5f4f' : (darkMode ? '#495057' : '#ddd') }}>
                            <div className="text-center">
                                <h6>🎮 Kick</h6>
                                {userConnections.kick ? (
                                    <>
                                        <Alert variant="success" className="mb-2">
                                            <small>✅ Verbunden</small>
                                        </Alert>
                                        <Button size="sm" variant="outline-danger" onClick={() => handleDisconnectService('kick')}>
                                            🔌 Trennen
                                        </Button>
                                    </>
                                ) : (
                                    <Button size="sm" variant="success" onClick={() => handleConnectService('kick')}>
                                        🔗 Verbinden
                                    </Button>
                                )}
                            </div>
                        </Card>
                    </Col>
                    <Col md={4} className="mb-2">
                        <Card className="p-3" style={{ backgroundColor: userConnections.youtube ? (darkMode ? '#4a2929' : '#ffebee') : (darkMode ? '#333' : '#f5f5f5'), borderColor: userConnections.youtube ? '#4a2929' : (darkMode ? '#495057' : '#ddd') }}>
                            <div className="text-center">
                                <h6>🎥 YouTube</h6>
                                {userConnections.youtube ? (
                                    <>
                                        <Alert variant="success" className="mb-2">
                                            <small>✅ Verbunden</small>
                                        </Alert>
                                        <Button size="sm" variant="outline-danger" onClick={() => handleDisconnectService('youtube')}>
                                            🔌 Trennen
                                        </Button>
                                    </>
                                ) : (
                                    <Button size="sm" variant="danger" onClick={() => handleConnectService('youtube')}>
                                        🔗 Verbinden
                                    </Button>
                                )}
                            </div>
                        </Card>
                    </Col>
                </Row>

                <hr />
                <h5>🔑 Passwort ändern</h5>
                {!showPasswordForm ? (
                    <Button variant="outline-primary" onClick={() => setShowPasswordForm(true)}>
                        Passwort ändern
                    </Button>
                ) : (
                    <>
                        {passwordMessage && (
                            <Alert variant={passwordMessage.type}>{passwordMessage.text}</Alert>
                        )}
                        <Form className="mt-3">
                            <Row className="mb-3">
                                <Col md={4}>
                                    <Form.Label>Aktuelles Passwort</Form.Label>
                                    <Form.Control
                                        type="password"
                                        value={oldPassword}
                                        onChange={(e) => setOldPassword(e.target.value)}
                                    />
                                </Col>
                                <Col md={4}>
                                    <Form.Label>Neues Passwort</Form.Label>
                                    <Form.Control
                                        type="password"
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                    />
                                </Col>
                                <Col md={4}>
                                    <Form.Label>Wiederholen</Form.Label>
                                    <Form.Control
                                        type="password"
                                        value={repeatPassword}
                                        onChange={(e) => setRepeatPassword(e.target.value)}
                                    />
                                </Col>
                            </Row>
                            <Button variant="success" onClick={handlePasswordChange}>
                                ✅ Speichern
                            </Button>{' '}
                            <Button variant="secondary" onClick={() => setShowPasswordForm(false)}>
                                Abbrechen
                            </Button>
                        </Form>
                    </>
                )}
            </Card>

            {/* ToastContainer für Erfolgsmeldungen */}
            <ToastContainer position="bottom-end" className="p-3">
                <Toast
                    onClose={() => setShowToast(false)}
                    show={showToast}
                    delay={3000}
                    autohide
                    bg={toastBG}
                >
                    <Toast.Body className="text-white">{toastMessage}</Toast.Body>
                </Toast>
            </ToastContainer>
        </>
    );

}

export default UserInterface;